import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import legalDocs from "../generated/legalDocumentsContent.json";

const SUPPORT_EMAIL = "support@burosoul.ru";
const OWNER_NAME = "ИП Козырева Валерия Витальевна";
const OWNER_INN = "602716479144";
const OWNER_OGRNIP = "326600000000411";

function normalizeDocSlug(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\.pdf$/i, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");
}

const LEGAL_DOCS = legalDocs;

const DOC_ALIASES = {
  oferta: "public-offer",
  offer: "public-offer",
  "public-offer": "public-offer",
  "public-offer-pdf": "public-offer",
  terms: "user-agreement",
  agreement: "user-agreement",
  "user-agreement": "user-agreement",
  "user-agreement-pdf": "user-agreement",
  privacy: "privacy-policy",
  "privacy-policy": "privacy-policy",
  "privacy-policy-pdf": "privacy-policy",
  "personal-data": "personal-data",
  "personal-data-processing": "personal-data",
  "personal-data-pdf": "personal-data",
  refund: "refund-policy",
  returns: "refund-policy",
  "refund-policy": "refund-policy",
  "refund-policy-pdf": "refund-policy",
  "return-policy": "refund-policy"
};

function resolveDocument(rawSlug) {
  const normalized = normalizeDocSlug(rawSlug);
  return DOC_ALIASES[normalized] || (LEGAL_DOCS[normalized] ? normalized : "");
}

function DocumentNav({ active }) {
  return (
    <aside className="legal-doc__nav" aria-label="Документы">
      <div className="legal-doc__nav-title">Документы</div>
      {Object.entries(LEGAL_DOCS).map(([slug, doc]) => (
        <Link key={slug} to={`/documents/${slug}`} className={`legal-doc__nav-link${active === slug ? " is-active" : ""}`}>
          <span>{doc.short}</span>
          <small>{doc.badge}</small>
        </Link>
      ))}
    </aside>
  );
}

export default function LegalDocument() {
  const navigate = useNavigate();
  const { docSlug } = useParams();
  const resolvedSlug = resolveDocument(docSlug);
  const doc = resolvedSlug ? LEGAL_DOCS[resolvedSlug] : null;
  const contentLines = String(doc?.content || "").split("\n");

  if (!doc) {
    return (
      <main className="legal-doc legal-doc--missing">
        <Helmet prioritizeSeoTags>
          <title>Документ не найден | Бюро Души</title>
          <meta name="description" content="Документ Бюро Души не найден. Выберите нужный документ из списка." />
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="legal-doc__wrap">
          <DocumentNav active="" />
          <section className="legal-doc__paper">
            <div className="legal-doc__badge">Документы</div>
            <h1>Документ не найден</h1>
            <p className="legal-doc__lead">Ссылка могла быть старой. Выберите нужный документ в списке слева или напишите в поддержку.</p>
            <div className="legal-doc__actions">
              <button type="button" className="legal-doc__btn legal-doc__btn--ghost" onClick={() => navigate(-1)}>Назад</button>
              <a className="legal-doc__btn" href={`mailto:${SUPPORT_EMAIL}`}>Написать в поддержку</a>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className={`legal-doc legal-doc--${resolvedSlug}`}>
      <Helmet prioritizeSeoTags>
        <title>{doc.title} | Бюро Души</title>
        <meta name="description" content={doc.description} />
      </Helmet>
      <div className="legal-doc__wrap">
        <DocumentNav active={resolvedSlug} />
        <article className="legal-doc__paper">
          <div className="legal-doc__topline">
            <span className="legal-doc__badge">{doc.badge}</span>
            <span className="legal-doc__date">{doc.metaLines?.[1] || "Актуальная редакция документа"}</span>
          </div>
          <h1>{doc.displayTitle || doc.title}</h1>
          <p className="legal-doc__lead">{doc.description}</p>

          <div className="legal-doc__company">
            <div>
              <strong>{OWNER_NAME}</strong>
              <span>ИНН {OWNER_INN}</span>
              <span>ОГРНИП {OWNER_OGRNIP}</span>
            </div>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </div>

          <section className="legal-doc__section legal-doc__section--text">
            <div className="legal-doc__meta">
              {Array.isArray(doc.metaLines) ? doc.metaLines.map((line) => (
                <span key={line}>{line}</span>
              )) : null}
            </div>
            <div className="legal-doc__text">
              {contentLines.map((line, index) => (
                <React.Fragment key={`${resolvedSlug}-${index}`}>
                  {line}
                  {index < contentLines.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </div>
          </section>

          <div className="legal-doc__actions legal-doc__actions--bottom">
            <button type="button" className="legal-doc__btn legal-doc__btn--ghost" onClick={() => navigate(-1)}>Назад</button>
            <a className="legal-doc__btn legal-doc__btn--ghost" href={doc.file} target="_blank" rel="noreferrer">
              Открыть PDF
            </a>
          </div>
        </article>
      </div>
    </main>
  );
}
